import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// import { useAuth } from '../AuthContext'; // Future use for user-specific features
import { useToasts } from '../components/ToastContext';
import { 
  FiClock, 
  FiTarget, 
  FiTrendingUp, 
  FiBook, 
  FiChevronDown, 
  FiChevronUp,
  FiCheck,
  FiX,
  FiEdit3,
  FiPlus,
  FiDownload,
  FiPrinter,
  FiRefreshCw,
  FiHome,
  FiFilter,
  FiEye,
  FiEyeOff
} from 'react-icons/fi';
import api from '../services/api';
import ConfidenceMeter from '../components/ConfidenceMeter';
import NoteModal from '../components/NoteModal';
import '../css/QuizResults.css';

function formatTime(seconds) {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatDate(timestamp) {
  return new Date(timestamp).toLocaleString(undefined, { 
    dateStyle: "medium", 
    timeStyle: "short" 
  });
}

function ResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  // const { user } = useAuth(); // Future use for user-specific features
  const { showToast } = useToasts();
  
  // Extract data from location state (for immediate post-quiz results)
  const {
    questions = [],
    userAnswers = [],
    settings = {},
    questionNotes = {},
    questionConfidence = {},
    elapsed = 0,
  } = location.state || {};

  // State for past quiz results mode
  const [pastQuizzes, setPastQuizzes] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedQuizId, setSelectedQuizId] = useState(null);
  const [selectedQuizDetails, setSelectedQuizDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // State management (existing)
  const [expandedIndexes, setExpandedIndexes] = useState([]);
  const [confidenceFilter, setConfidenceFilter] = useState('all');
  const [answerFilter, setAnswerFilter] = useState('all'); // all, correct, incorrect
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [categoryStats, setCategoryStats] = useState({});

  // Determine if we're in "past results" mode (no location.state) or "current results" mode
  const isPastResultsMode = !questions.length && !location.state;

  // Load quiz history for past results mode
  useEffect(() => {
    if (isPastResultsMode) {
      const loadQuizHistory = async () => {
        setIsLoadingHistory(true);
        try {
          const response = await api.get('/quiz/history?limit=20&sort_by=date&sort_order=desc');
          setPastQuizzes(response.data.quizzes || []);
        } catch (error) {
          console.error('Failed to load quiz history:', error);
          showToast('Failed to load quiz history', 'error');
        } finally {
          setIsLoadingHistory(false);
        }
      };

      loadQuizHistory();
    }
  }, [isPastResultsMode, showToast]);

  // Load detailed quiz results for selected past quiz
  const loadQuizDetails = async (quizId) => {
    setIsLoadingDetails(true);
    try {
      // FIXED: Call the completion endpoint that we just fixed, not the quiz details endpoint
      const response = await api.post(`/quiz/${quizId}/complete`, {
        time_spent_total: 0  // Required field, but not relevant for past results
      });
      const quizData = response.data.results;
      
      // Transform backend data to match ResultsPage format
      const transformedQuestions = quizData.questions.map(q => ({
        question_id: q.question_id,
        question_text: q.question_text,
        choice_a: q.choice_a,
        choice_b: q.choice_b,
        choice_c: q.choice_c,
        choice_d: q.choice_d,
        choice_e: q.choice_e,
        correct_choice: q.correct_choice,  // Now properly retrieved from Questions table
        rationale: q.rationale,
        category_name: q.category_name,
        media_url: q.media_url
      }));

      const transformedAnswers = quizData.questions.map(q => 
        q.selected_answer || null  // User's selected answer from Quiz_Answers
      );

      const transformedNotes = {};
      const transformedConfidence = {};
      quizData.questions.forEach((q, idx) => {
        if (q.notes) {
          transformedNotes[idx] = q.notes;
        }
        if (q.confidence_level) {
          transformedConfidence[idx] = q.confidence_level;
        }
      });

      setSelectedQuizDetails({
        questions: transformedQuestions,
        userAnswers: transformedAnswers,
        settings: quizData.settings || {},
        questionNotes: transformedNotes,
        questionConfidence: transformedConfidence,
        elapsed: quizData.elapsed_seconds || 0,
        completedAt: quizData.completed_at,
        score: quizData.score
      });

      setSelectedQuizId(quizId);
    } catch (error) {
      console.error('Failed to load quiz details:', error);
      showToast('Failed to load quiz details', 'error');
    } finally {
      setIsLoadingDetails(false);
    }
  };;

  // Use current quiz data or selected past quiz data
  const activeQuestions = selectedQuizDetails?.questions || questions;
  const activeUserAnswers = selectedQuizDetails?.userAnswers || userAnswers;
  const activeSettings = selectedQuizDetails?.settings || settings;
  const activeQuestionNotes = selectedQuizDetails?.questionNotes || questionNotes;
  const activeQuestionConfidence = selectedQuizDetails?.questionConfidence || questionConfidence;
  const activeElapsed = selectedQuizDetails?.elapsed || elapsed;

  // Memoized calculations (updated to use active data)
  const scoreData = useMemo(() => {
    if (!activeQuestions.length) return { correct: 0, total: 0, percentage: 0 };
    
    const correct = activeQuestions.reduce((count, question, idx) => {
      return activeUserAnswers[idx] === question.correct_choice ? count + 1 : count;
    }, 0);
    
    return {
      correct,
      total: activeQuestions.length,
      percentage: Math.round((correct / activeQuestions.length) * 100)
    };
  }, [activeQuestions, activeUserAnswers]);

  const streakData = useMemo(() => {
    let bestStreak = 0;
    let currentStreak = 0;
    
    activeQuestions.forEach((question, i) => {
      if (activeUserAnswers[i] === question.correct_choice) {
        currentStreak++;
        bestStreak = Math.max(bestStreak, currentStreak);
      } else {
        currentStreak = 0;
      }
    });
    
    return { best: bestStreak, current: currentStreak };
  }, [activeQuestions, activeUserAnswers]);

  const timeData = useMemo(() => {
    const totalTime = activeElapsed;
    const avgTime = totalTime && activeQuestions.length ? totalTime / activeQuestions.length : 0;
    const timePerQuestion = activeSettings?.timePerQuestion || [];
    
    return {
      total: totalTime,
      average: avgTime,
      perQuestion: timePerQuestion
    };
  }, [activeElapsed, activeQuestions.length, activeSettings?.timePerQuestion]);

  // Build category statistics (updated to use active data)
  useEffect(() => {
    if (!activeQuestions.length) return;
    
    const stats = {};
    activeQuestions.forEach((question, idx) => {
      const category = question.category_name || 'Other';
      if (!stats[category]) {
        stats[category] = { total: 0, correct: 0, percentage: 0 };
      }
      stats[category].total++;
      if (activeUserAnswers[idx] === question.correct_choice) {
        stats[category].correct++;
      }
    });
    
    // Calculate percentages
    Object.keys(stats).forEach(category => {
      stats[category].percentage = Math.round((stats[category].correct / stats[category].total) * 100);
    });
    
    setCategoryStats(stats);
  }, [activeQuestions, activeUserAnswers]);

  // Save results to backend (only for current quiz results, not past results)
  useEffect(() => {
    if (!questions.length || !userAnswers.length || isPastResultsMode) return;
    
    const saveResults = async () => {
      try {
        await api.post('/quiz/results', {
          questions,
          userAnswers,
          settings,
          questionNotes,
          questionConfidence,
          elapsed
        });
        showToast('Results saved successfully!', 'success');
      } catch (error) {
        console.error('Failed to save results:', error);
        showToast('Failed to save results', 'error');
      }
    };
    
    saveResults();
  }, [questions, userAnswers, settings, questionNotes, questionConfidence, elapsed, showToast, isPastResultsMode]);

  // Filter questions based on selected filters (updated to use active data)
  const filteredQuestions = useMemo(() => {
    return activeQuestions.map((question, index) => ({ question, index }))
      .filter(({ question, index }) => {
        // Confidence filter
        if (confidenceFilter !== 'all') {
          const confidence = activeQuestionConfidence[index] || 1;
          if (confidenceFilter === 'low' && confidence !== 1) return false;
          if (confidenceFilter === 'medium' && confidence !== 2) return false;
          if (confidenceFilter === 'high' && confidence !== 3) return false;
        }
        
        // Answer filter
        if (answerFilter !== 'all') {
          const isCorrect = activeUserAnswers[index] === question.correct_choice;
          if (answerFilter === 'correct' && !isCorrect) return false;
          if (answerFilter === 'incorrect' && isCorrect) return false;
        }
        
        return true;
      });
  }, [activeQuestions, confidenceFilter, answerFilter, activeQuestionConfidence, activeUserAnswers]);

  // Event handlers (updated to use active data)
  const handleToggleExpanded = (index) => {
    setExpandedIndexes(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handleExpandAll = () => {
    setExpandedIndexes(filteredQuestions.map(({ index }) => index));
  };

  const handleCollapseAll = () => {
    setExpandedIndexes([]);
  };

  const handleOpenNoteModal = (index) => {
    setEditingNoteIndex(index);
    setNoteModalOpen(true);
  };

  const handleSaveNote = (note) => {
    if (editingNoteIndex !== null) {
      activeQuestionNotes[editingNoteIndex] = note;
      setNoteModalOpen(false);
      setEditingNoteIndex(null);
      showToast('Note saved!', 'success');
    }
  };

  const handleExport = () => {
    const exportData = {
      quiz: {
        mode: activeSettings?.mode || 'practice',
        date: formatDate(selectedQuizDetails?.completedAt || activeSettings?.dateTaken || Date.now()),
        questions: activeQuestions.length,
        timeLimit: activeSettings?.timeLimit,
        score: selectedQuizDetails?.score || scoreData.percentage
      },
      results: {
        score: `${scoreData.percentage}%`,
        correct: scoreData.correct,
        total: scoreData.total,
        bestStreak: streakData.best,
        totalTime: formatTime(timeData.total),
        avgTime: `${timeData.average.toFixed(1)}s`
      },
      categories: categoryStats,
      details: activeQuestions.map((question, index) => ({
        questionNumber: index + 1,
        questionId: question.question_id,
        // Proprietary content removed for IP protection
        userAnswerLetter: activeUserAnswers[index],
        isCorrect: activeUserAnswers[index] === question.correct_choice,
        category: question.category_name,
        confidence: activeQuestionConfidence[index] || null,
        note: activeQuestionNotes[index] || '',
        timeSpent: timeData.perQuestion[index] || 0
      }))
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
      type: "application/json" 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `quiz-results-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('Results exported!', 'success');
  };

  const handlePrint = () => {
    // Create a secure print version with NO proprietary question content
    const safePrintData = {
      quiz: {
        mode: activeSettings?.mode || 'practice',
        date: formatDate(selectedQuizDetails?.completedAt || activeSettings?.dateTaken || Date.now()),
        questions: activeQuestions.length,
        timeLimit: activeSettings?.timeLimit,
        score: selectedQuizDetails?.score || scoreData.percentage
      },
      results: {
        score: `${scoreData.percentage}%`,
        correct: scoreData.correct,
        total: scoreData.total,
        bestStreak: streakData.best,
        totalTime: formatTime(timeData.total),
        avgTime: `${timeData.average.toFixed(1)}s`
      },
      categories: categoryStats,
      details: activeQuestions.map((question, index) => ({
        questionNumber: index + 1,
        questionId: question.question_id,
        // NO proprietary content included
        userAnswerLetter: activeUserAnswers[index],
        isCorrect: activeUserAnswers[index] === question.correct_choice,
        category: question.category_name,
        confidence: activeQuestionConfidence[index] || null,
        note: activeQuestionNotes[index] || '',
        timeSpent: timeData.perQuestion[index] || 0
      })),
      exportedAt: new Date().toISOString()
    };

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>PA Quiz Results Summary</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.4; }
            .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
            .summary-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            .summary-table th, .summary-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .summary-table th { background-color: #f2f2f2; font-weight: bold; }
            .score-summary { display: flex; gap: 30px; margin: 20px 0; }
            .score-item { text-align: center; }
            .score-value { font-size: 24px; font-weight: bold; color: #2563eb; }
            .score-label { color: #666; margin-top: 5px; }
            .category-section { margin: 20px 0; }
            .no-proprietary { background: #fff3cd; border: 1px solid #ffeaa7; padding: 10px; margin: 20px 0; }
            .correct { color: #16a34a; }
            .incorrect { color: #dc2626; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>PA Quiz Results Summary</h1>
            <p><strong>Mode:</strong> ${safePrintData.quiz.mode}</p>
            <p><strong>Date:</strong> ${safePrintData.quiz.date}</p>
            <p><strong>Questions:</strong> ${safePrintData.quiz.questions}</p>
            <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
          </div>
          
          <div class="no-proprietary">
            <strong>Privacy Notice:</strong> This printout contains only question IDs, categories, and your performance data. 
            No proprietary question content, answer choices, or explanations are included to protect intellectual property.
          </div>

          <div class="score-summary">
            <div class="score-item">
              <div class="score-value">${safePrintData.results.score}</div>
              <div class="score-label">Final Score</div>
            </div>
            <div class="score-item">
              <div class="score-value">${safePrintData.results.correct}/${safePrintData.results.total}</div>
              <div class="score-label">Correct Answers</div>
            </div>
            <div class="score-item">
              <div class="score-value">${safePrintData.results.totalTime}</div>
              <div class="score-label">Total Time</div>
            </div>
            <div class="score-item">
              <div class="score-value">${safePrintData.results.bestStreak}</div>
              <div class="score-label">Best Streak</div>
            </div>
          </div>

          ${Object.keys(safePrintData.categories).length > 0 ? `
            <div class="category-section">
              <h2>Category Performance</h2>
              <table class="summary-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Correct</th>
                    <th>Total</th>
                    <th>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  ${Object.entries(safePrintData.categories).map(([category, stats]) => `
                    <tr>
                      <td>${category}</td>
                      <td>${stats.correct}</td>
                      <td>${stats.total}</td>
                      <td>${stats.percentage}%</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}

          <h2>Question-by-Question Summary</h2>
          <table class="summary-table">
            <thead>
              <tr>
                <th>Question #</th>
                <th>Question ID</th>
                <th>Category</th>
                <th>Your Answer</th>
                <th>Result</th>
                <th>Confidence</th>
                <th>Has Note</th>
              </tr>
            </thead>
            <tbody>
              ${safePrintData.details.map(q => `
                <tr>
                  <td>${q.questionNumber}</td>
                  <td>${q.questionId}</td>
                  <td>${q.category}</td>
                  <td>${q.userAnswerLetter || 'No answer'}</td>
                  <td class="${q.isCorrect ? 'correct' : 'incorrect'}">
                    ${q.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                  </td>
                  <td>${q.confidence === null ? 'Not set' : 
                        q.confidence === 1 ? 'Low' : 
                        q.confidence === 2 ? 'Medium' : 'High'}</td>
                  <td>${q.note ? 'Yes' : 'No'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };

  const handleRetakeQuiz = () => {
    navigate('/quiz', { state: { settings: activeSettings } });
  };

  const handleReviewIncorrect = () => {
    const incorrectQuestions = activeQuestions.filter((question, idx) => 
      activeUserAnswers[idx] !== question.correct_choice
    );
    
    if (incorrectQuestions.length === 0) {
      showToast('No incorrect questions to review!', 'info');
      return;
    }
    
    navigate('/review', { state: { questions: incorrectQuestions } });
  };

  const getChoiceText = (question, choiceKey) => {
    if (!choiceKey) return 'No answer selected';
    return question[`choice_${choiceKey.toLowerCase()}`] || 'N/A';
  };

  const handleBackToPastResults = () => {
    setSelectedQuizId(null);
    setSelectedQuizDetails(null);
    setExpandedIndexes([]);
  };

  // Past Results Mode - Show quiz history
  if (isPastResultsMode && !selectedQuizId) {
    return (
      <div className="results-page">
        <div className="container">
          <div className="results-header">
            <div className="header-content">
              <h1>Past Quiz Results</h1>
              <p className="subtitle">View your quiz history and detailed results</p>
            </div>
            <div className="header-actions">
              <button 
                className="btn btn-outline"
                onClick={() => {
                  console.log('Past results dashboard button clicked, navigating to /dashboard');
                  navigate('/dashboard');
                }}
              >
                <FiHome size={16} />
                Dashboard
              </button>
            </div>
          </div>

          {isLoadingHistory ? (
            <div className="card">
              <div className="card-content text-center">
                <p>Loading quiz history...</p>
              </div>
            </div>
          ) : pastQuizzes.length === 0 ? (
            <div className="card">
              <div className="card-content text-center">
                <h2>No Quiz History</h2>
                <p>You haven't completed any quizzes yet.</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate('/quiz')}
                >
                  <FiTarget size={16} />
                  Take Your First Quiz
                </button>
              </div>
            </div>
          ) : (
            <div className="past-quizzes-list">
              {pastQuizzes.map((quiz) => (
                <div key={quiz.quiz_id} className="card quiz-history-item">
                  <div className="card-content">
                    <div className="quiz-header">
                      <div className="quiz-info">
                        <h3>{quiz.mode?.charAt(0).toUpperCase() + quiz.mode?.slice(1)} Quiz</h3>
                        <p className="quiz-date">{formatDate(quiz.completed_at)}</p>
                      </div>
                      <div className="quiz-score">
                        <div className={`score-badge ${quiz.passed ? 'passed' : 'failed'}`}>
                          {quiz.score}%
                        </div>
                      </div>
                    </div>
                    
                    <div className="quiz-stats">
                      <div className="stat-item">
                        <FiTarget size={16} />
                        <span>{quiz.score}% ({Math.round(quiz.question_count * quiz.score / 100)}/{quiz.question_count})</span>
                      </div>
                      <div className="stat-item">
                        <FiClock size={16} />
                        <span>{formatTime(quiz.time_taken_seconds)}</span>
                      </div>
                      <div className="stat-item">
                        <FiBook size={16} />
                        <span>{quiz.difficulty || 'Mixed'} difficulty</span>
                      </div>
                    </div>

                    <div className="quiz-actions">
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => loadQuizDetails(quiz.quiz_id)}
                        disabled={isLoadingDetails}
                      >
                        {isLoadingDetails ? 'Loading...' : 'View Details'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Guard clause for no data
  if (!activeQuestions.length && !isPastResultsMode) {
    return (
      <div className="results-page">
        <div className="container">
          <div className="card">
            <div className="card-content text-center">
              <h1>No Results Available</h1>
              <p>We couldn't find any quiz results to display.</p>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  console.log('No results dashboard button clicked, navigating to /dashboard');
                  navigate('/dashboard');
                }}
              >
                <FiHome size={16} />
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="results-page">
      <div className="container">
        {/* Header Section */}
        <div className="results-header">
          <div className="header-content">
            <h1>Quiz Results</h1>
            <p className="subtitle">
              {activeSettings?.mode === 'test' ? 'Test Mode' : 'Practice Mode'} • 
              {formatDate(selectedQuizDetails?.completedAt || activeSettings?.dateTaken || Date.now())}
            </p>
            {selectedQuizId && (
              <button 
                className="btn btn-ghost btn-sm"
                onClick={handleBackToPastResults}
              >
                ← Back to Past Results
              </button>
            )}
          </div>
          
          <div className="header-actions">
            <button className="btn btn-outline" onClick={handleExport}>
              <FiDownload size={16} />
              Export
            </button>
            <button className="btn btn-outline" onClick={handlePrint}>
              <FiPrinter size={16} />
              Print
            </button>
          </div>
        </div>

        {/* Score Overview Cards */}
        <div className="score-overview">
          <div className="score-card primary">
            <div className="score-icon">
              <FiTarget size={24} />
            </div>
            <div className="score-content">
              <div className="score-value">{scoreData.percentage}%</div>
              <div className="score-label">Final Score</div>
              <div className="score-detail">{scoreData.correct} of {scoreData.total} correct</div>
            </div>
          </div>
          
          <div className="score-card">
            <div className="score-icon">
              <FiClock size={24} />
            </div>
            <div className="score-content">
              <div className="score-value">{formatTime(timeData.total)}</div>
              <div className="score-label">Total Time</div>
              <div className="score-detail">Avg: {timeData.average.toFixed(1)}s per question</div>
            </div>
          </div>
          
          <div className="score-card">
            <div className="score-icon">
              <FiTrendingUp size={24} />
            </div>
            <div className="score-content">
              <div className="score-value">{streakData.best}</div>
              <div className="score-label">Best Streak</div>
              <div className="score-detail">Correct answers in a row</div>
            </div>
          </div>
        </div>

        {/* Category Breakdown */}
        {Object.keys(categoryStats).length > 0 && (
          <div className="card">
            <div className="card-header">
              <h3>
                <FiBook size={20} />
                Category Performance
              </h3>
            </div>
            <div className="card-content">
              <div className="category-grid">
                {Object.entries(categoryStats).map(([category, stats]) => (
                  <div key={category} className="category-item">
                    <div className="category-name">{category}</div>
                    <div className="category-score">
                      {stats.correct}/{stats.total}
                    </div>
                    <div className="category-percentage">{stats.percentage}%</div>
                    <div className="category-bar">
                      <div 
                        className="category-progress" 
                        style={{ width: `${stats.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-section">
          <div className="action-buttons">
            <button className="btn btn-primary" onClick={handleRetakeQuiz}>
              <FiRefreshCw size={16} />
              Retake Quiz
            </button>
            <button className="btn btn-secondary" onClick={handleReviewIncorrect}>
              <FiX size={16} />
              Review Incorrect ({activeQuestions.length - scoreData.correct})
            </button>
            <button 
              className="btn btn-outline"
              onClick={() => {
                console.log('Dashboard button clicked, navigating to /dashboard');
                navigate('/dashboard');
              }}
            >
              <FiHome size={16} />
              Dashboard
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card">
          <div className="card-header">
            <h3>
              <FiFilter size={20} />
              Question Review
            </h3>
            <div className="filter-actions">
              <button 
                className={`btn btn-sm ${expandedIndexes.length === filteredQuestions.length ? 'btn-outline' : 'btn-ghost'}`}
                onClick={expandedIndexes.length === filteredQuestions.length ? handleCollapseAll : handleExpandAll}
              >
                {expandedIndexes.length === filteredQuestions.length ? (
                  <>
                    <FiEyeOff size={14} />
                    Collapse All
                  </>
                ) : (
                  <>
                    <FiEye size={14} />
                    Expand All
                  </>
                )}
              </button>
            </div>
          </div>
          
          <div className="card-content">
            <div className="filters">
              <div className="filter-group">
                <label>Answer Status:</label>
                <select 
                  value={answerFilter} 
                  onChange={(e) => setAnswerFilter(e.target.value)}
                  className="form-select"
                >
                  <option value="all">All Questions</option>
                  <option value="correct">Correct Only</option>
                  <option value="incorrect">Incorrect Only</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>Confidence Level:</label>
                <select 
                  value={confidenceFilter} 
                  onChange={(e) => setConfidenceFilter(e.target.value)}
                  className="form-select"
                >
                  <option value="all">All Levels</option>
                  <option value="low">Low Confidence</option>
                  <option value="medium">Medium Confidence</option>
                  <option value="high">High Confidence</option>
                </select>
              </div>
              
              <div className="filter-summary">
                Showing {filteredQuestions.length} of {activeQuestions.length} questions
              </div>
            </div>
          </div>
        </div>

        {/* Question Review */}
        <div className="question-review">
          {filteredQuestions.map(({ question, index }) => {
            const isCorrect = activeUserAnswers[index] === question.correct_choice;
            const isExpanded = expandedIndexes.includes(index);
            const userAnswerText = getChoiceText(question, activeUserAnswers[index]);
            const correctAnswerText = getChoiceText(question, question.correct_choice);
            const confidence = activeQuestionConfidence[index] || 1;
            const note = activeQuestionNotes[index];
            
            return (
              <div key={question.question_id} className={`question-card ${isCorrect ? 'correct' : 'incorrect'}`}>
                <div 
                  className="question-header" 
                  onClick={() => handleToggleExpanded(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleToggleExpanded(index);
                    }
                  }}
                >
                  <div className="question-status">
                    {isCorrect ? (
                      <FiCheck size={20} className="status-correct" />
                    ) : (
                      <FiX size={20} className="status-incorrect" />
                    )}
                  </div>
                  
                  <div className="question-content">
                    <div className="question-number">Question {index + 1}</div>
                    <div className="question-text">{question.question_text}</div>
                    <div className="question-meta">
                      <span className="answer-preview">
                        Your answer: <strong>{userAnswerText}</strong>
                      </span>
                      {!isCorrect && (
                        <span className="correct-preview">
                          Correct: <strong>{correctAnswerText}</strong>
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="question-toggle">
                    {isExpanded ? (
                      <FiChevronUp size={20} />
                    ) : (
                      <FiChevronDown size={20} />
                    )}
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="question-details">
                    {/* Media */}
                    {question.media_url && (
                      <div className="question-media">
                        {question.media_url.match(/\.(jpg|jpeg|png|gif)$/i) && (
                          <img 
                            src={question.media_url} 
                            alt={question.media_alt || "Question media"} 
                            className="media-image"
                          />
                        )}
                        {question.media_url.match(/\.(mp3|wav|ogg)$/i) && (
                          <audio controls src={question.media_url} className="media-audio" />
                        )}
                        {question.media_url.match(/\.(mp4|webm|mov)$/i) && (
                          <video controls src={question.media_url} className="media-video" />
                        )}
                      </div>
                    )}
                    
                    {/* Answer Details */}
                    <div className="answer-details">
                      <div className="answer-section">
                        <strong>Your Answer:</strong> {userAnswerText}
                      </div>
                      {!isCorrect && (
                        <div className="answer-section correct">
                          <strong>Correct Answer:</strong> {correctAnswerText}
                        </div>
                      )}
                      <div className="answer-section">
                        <strong>Category:</strong> {question.category_name || 'General'}
                      </div>
                    </div>

                    {/* All Answer Choices */}
                    <div className="answer-choices-section">
                      <h4>Answer Choices:</h4>
                      <div className="choices-list">
                        {['A', 'B', 'C', 'D', 'E'].map(choice => {
                          const choiceText = question[`choice_${choice.toLowerCase()}`];
                          if (!choiceText) return null;
                          
                          const isUserAnswer = activeUserAnswers[index] === choice;
                          const isCorrectAnswer = question.correct_choice === choice;
                          
                          return (
                            <div 
                              key={choice}
                              className={`choice-item ${isUserAnswer ? 'user-choice' : ''} ${isCorrectAnswer ? 'correct-choice' : ''} ${isUserAnswer && !isCorrectAnswer ? 'incorrect-choice' : ''}`}
                            >
                              <div className="choice-header">
                                <span className="choice-letter">{choice}.</span>
                                <div className="choice-indicators">
                                  {isUserAnswer && (
                                    <span className={`choice-indicator user ${isCorrect ? 'correct' : 'incorrect'}`}>
                                      {isCorrect ? '✓ Your Answer' : '✗ Your Answer'}
                                    </span>
                                  )}
                                  {isCorrectAnswer && (
                                    <span className="choice-indicator correct">✓ Correct Answer</span>
                                  )}
                                </div>
                              </div>
                              <div className="choice-text">{choiceText}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Tools */}
                    <div className="question-tools">
                      <div className="tool-group">
                        <strong>Confidence:</strong>
                        <ConfidenceMeter value={confidence} readOnly />
                      </div>
                      
                      <div className="tool-group">
                        {note ? (
                          <div className="note-display">
                            <strong>Note:</strong> {note}
                            <button 
                              className="btn btn-ghost btn-sm"
                              onClick={() => handleOpenNoteModal(index)}
                            >
                              <FiEdit3 size={14} />
                              Edit
                            </button>
                          </div>
                        ) : (
                          <button 
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleOpenNoteModal(index)}
                          >
                            <FiPlus size={14} />
                            Add Note
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Rationale */}
                    {question.rationale && (
                      <div className="rationale">
                        <h4>Explanation</h4>
                        <p>{question.rationale}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Note Modal */}
      <NoteModal
        isOpen={noteModalOpen}
        onClose={() => {
          setNoteModalOpen(false);
          setEditingNoteIndex(null);
        }}
        note={editingNoteIndex !== null ? activeQuestionNotes[editingNoteIndex] || '' : ''}
        onSave={handleSaveNote}
      />
    </div>
  );
}

export default ResultsPage;